import React, { useEffect, useState } from 'react';
import { Check, X, Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react';

// Structured editor for any set of multiple-choice questions: a quiz or exam
// artifact (format "json") and an A/V script's per-chapter knowledge checks.
//
// These were previously edited as raw JSON in a textarea, which is the same
// problem as editing prose as raw Markdown: the people using this compartment
// should never see the serialization format. Nothing here can produce invalid
// JSON, so the server's valid_quiz_json? guard can no longer reject a save.
//
// Knowledge checks reuse this rather than getting a lookalike, so they inherit the
// position-based answer tracking below, the two-choice floor, the reorder controls and
// the pre-emptive problems list. The two differ only in the name of the explanation
// field, how many blanks a new one starts with, and what saving means, so those are
// props.
function parseQuestions(content, explanationKey, defaultChoiceCount) {
  let parsed = null;
  try {
    parsed = typeof content === 'string' ? JSON.parse(content) : content;
  } catch {
    parsed = null;
  }
  const rows = Array.isArray(parsed) ? parsed
    : (Array.isArray(parsed?.questions) ? parsed.questions : []);
  return rows.map((q) => ({
    prompt: q?.prompt || '',
    choices: Array.isArray(q?.choices) && q.choices.length
      ? q.choices.map((c) => c ?? '')
      : Array(defaultChoiceCount).fill(''),
    answer_index: Number.isInteger(q?.answer_index) ? q.answer_index : 0,
    explanation: q?.[explanationKey] || '',
  }));
}

const blankQuestion = (choiceCount) => ({
  prompt: '', choices: Array(choiceCount).fill(''), answer_index: 0, explanation: '',
});

export default function QuizEditor({
  value, onSave, onCancel, saving,
  // A quiz calls it a rationale shown with the answer key; a knowledge check calls it
  // the response the learner sees when the answer is revealed.
  explanationKey = 'rationale',
  explanationLabel = 'Rationale (shown with the answer key)',
  explanationPlaceholder = 'Why is that the right answer?',
  // ZU's A/V template uses four choices; a quiz question starts with two.
  defaultChoiceCount = 2,
  // How many blanks an empty set starts with.
  seedCount = 1,
  footerNote = 'Saving returns this artifact to draft for re-approval.',
}) {
  const [questions, setQuestions] = useState(() => {
    const parsed = parseQuestions(value, explanationKey, defaultChoiceCount);
    return parsed.length ? parsed : Array.from({ length: seedCount },
                                               () => blankQuestion(defaultChoiceCount));
  });
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const mutate = (fn) => {
    setQuestions((qs) => {
      const next = fn(qs.map((q) => ({ ...q, choices: [...q.choices] })));
      return next;
    });
    setDirty(true);
  };

  const setField = (qi, field, v) => mutate((qs) => { qs[qi][field] = v; return qs; });
  const setChoice = (qi, ci, v) => mutate((qs) => { qs[qi].choices[ci] = v; return qs; });
  const addChoice = (qi) => mutate((qs) => { qs[qi].choices.push(''); return qs; });
  const removeChoice = (qi, ci) => mutate((qs) => {
    if (qs[qi].choices.length <= 2) return qs; // a choice question needs at least two
    qs[qi].choices.splice(ci, 1);
    if (qs[qi].answer_index >= qs[qi].choices.length) qs[qi].answer_index = 0;
    else if (ci < qs[qi].answer_index) qs[qi].answer_index -= 1;
    return qs;
  });
  const move = (qi, delta) => mutate((qs) => {
    const target = qi + delta;
    if (target < 0 || target >= qs.length) return qs;
    const [row] = qs.splice(qi, 1);
    qs.splice(target, 0, row);
    return qs;
  });

  // The server rejects an assessment with no questions, and a question with an
  // empty prompt is useless, so surface that here instead of as a failed save.
  const problems = [];
  if (questions.length === 0) problems.push('Add at least one question.');
  questions.forEach((q, i) => {
    if (!q.prompt.trim()) problems.push(`Question ${i + 1} has no prompt.`);
    if (q.choices.filter((c) => c.trim()).length < 2) {
      problems.push(`Question ${i + 1} needs at least two answer choices.`);
    }
    if (!q.choices[q.answer_index]?.trim()) {
      problems.push(`Question ${i + 1} has no correct answer selected.`);
    }
  });

  const label = 'text-[10px] uppercase tracking-wide text-[#7d8aa0]';
  const input = 'w-full text-xs bg-transparent border border-[#2b3a4d] rounded px-2 py-1 text-[#c7d0e0]';

  return (
    <div className="space-y-3">
      {questions.map((q, qi) => (
        <div key={qi} className="rounded-lg border border-[#2b3a4d] bg-[#0b0e13] p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className={label}>Question {qi + 1}</span>
            <div className="flex items-center gap-0.5">
              <button onClick={() => move(qi, -1)} disabled={qi === 0}
                className="p-1 rounded hover:bg-[#12202f] text-[#8a95a8] disabled:opacity-30"
                title="Move up"><ChevronUp size={13} /></button>
              <button onClick={() => move(qi, 1)} disabled={qi === questions.length - 1}
                className="p-1 rounded hover:bg-[#12202f] text-[#8a95a8] disabled:opacity-30"
                title="Move down"><ChevronDown size={13} /></button>
              <button
                onClick={() => {
                  if (window.confirm(`Delete question ${qi + 1}?`)) {
                    mutate((qs) => { qs.splice(qi, 1); return qs; });
                  }
                }}
                className="p-1 rounded hover:bg-[#12202f] text-[#f7a23b]"
                title="Delete this question"><Trash2 size={13} /></button>
            </div>
          </div>

          <textarea value={q.prompt} onChange={(e) => setField(qi, 'prompt', e.target.value)}
            rows={2} placeholder="What is the question?" className={input} />

          <div className="space-y-1">
            <span className={label}>Answer choices (select the correct one)</span>
            {q.choices.map((c, ci) => (
              <div key={ci} className="flex items-center gap-2">
                <input
                  type="radio"
                  name={`answer-${qi}`}
                  checked={q.answer_index === ci}
                  onChange={() => setField(qi, 'answer_index', ci)}
                  title="Mark this as the correct answer"
                />
                <span className="text-[11px] text-[#5f6b7e] w-4">
                  {String.fromCharCode(65 + ci)}.
                </span>
                <input value={c} onChange={(e) => setChoice(qi, ci, e.target.value)}
                  placeholder={`Choice ${String.fromCharCode(65 + ci)}`} className={input} />
                <button onClick={() => removeChoice(qi, ci)} disabled={q.choices.length <= 2}
                  className="p-1 rounded hover:bg-[#12202f] text-[#f7a23b] disabled:opacity-30"
                  title={q.choices.length <= 2 ? 'A question needs at least two choices' : 'Remove this choice'}>
                  <X size={12} />
                </button>
              </div>
            ))}
            <button onClick={() => addChoice(qi)} className="text-[11px] text-[#5ea9ff]">
              + add a choice
            </button>
          </div>

          <div>
            <span className={label}>{explanationLabel}</span>
            <textarea value={q.explanation} onChange={(e) => setField(qi, 'explanation', e.target.value)}
              rows={2} placeholder={explanationPlaceholder} className={input} />
          </div>
        </div>
      ))}

      <button onClick={() => mutate((qs) => { qs.push(blankQuestion(defaultChoiceCount)); return qs; })}
        className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-[#2b3a4d] text-[#5ea9ff]">
        <Plus size={12} /> Add a question
      </button>

      {problems.length > 0 && (
        <ul className="text-[11px] text-[#f7a23b] list-disc pl-4 space-y-0.5">
          {problems.slice(0, 4).map((p, i) => <li key={i}>{p}</li>)}
        </ul>
      )}

      <div className="flex items-center gap-2 border-t border-[#1d2634] pt-3">
        <button
          onClick={() => {
            setDirty(false);
            // Hands back the normalized rows. Serializing here is what made this
            // component quiz-shaped: the quiz call site stringifies { questions },
            // and the knowledge-check call site patches one chapter.
            onSave(questions.map((q) => {
                // Track the correct answer by its POSITION, never by its text.
                // Matching on text (indexOf) silently marks the first duplicate
                // correct: choices ["Yes","No","Yes"] with the third selected
                // saved index 0, so an exam quietly graded the wrong option.
                const kept = q.choices
                  .map((c, i) => ({ text: c, index: i }))
                  .filter((x) => x.text.trim());
                const answerAt = kept.findIndex((x) => x.index === q.answer_index);
              return {
                prompt: q.prompt.trim(),
                choices: kept.map((x) => x.text),
                answer_index: answerAt >= 0 ? answerAt : 0,
                [explanationKey]: q.explanation.trim() || null,
              };
            }));
          }}
          disabled={problems.length > 0 || saving}
          className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-[#1f6feb] text-white disabled:opacity-40"
        >
          <Check size={12} /> {saving ? 'Saving' : 'Save'}
        </button>
        <button
          onClick={() => {
            if (dirty && !window.confirm('Discard your unsaved changes?')) return;
            onCancel();
          }}
          className="flex items-center gap-1 text-xs px-3 py-1.5 text-[#8a95a8]"
        >
          <X size={12} /> Cancel
        </button>
        <span className="text-[10px] text-[#5f6b7e] ml-2">
          {questions.length} question{questions.length === 1 ? '' : 's'}. {footerNote}
        </span>
      </div>
    </div>
  );
}
